import { Icons } from '@shared/components/content/icons';
import { cn } from '@shared/lib/utils';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface DataWithDrillthroughCellProps {
  /** Where this cell navigates to when clicked. Required — this cell exists for that purpose. */
  link: string;
  /** Cell content. Usually a NumberCell, Text, etc. */
  children: ReactNode;
  /** Optional class merged onto the link wrapper (e.g. text-right for number columns). */
  className?: string;
  /** Optional aria-label for screen readers. */
  ariaLabel?: string;
}

/**
 * A standard "this number / value is a drill-through" cell.
 *
 * Wraps its children in a react-router Link styled as a subtle inline link
 * with a faint chevron after the value to signal clickability. Use anywhere
 * a table cell value should be clickable to navigate elsewhere (e.g. the
 * per-org counts on the group dashboard Organisations table).
 */
export function DataWithDrillthroughCell({
  link,
  children,
  className,
  ariaLabel,
}: DataWithDrillthroughCellProps) {
  return (
    <Link
      to={link}
      aria-label={ariaLabel}
      className={cn(
        'group/cell flex w-full items-center gap-1 font-medium text-teal-600',
        className
      )}
    >
      <span className="flex-1">{children}</span>
      <Icons.chevronRight
        aria-hidden
        className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover/cell:opacity-100 group-focus-visible/cell:opacity-100"
      />
    </Link>
  );
}
